/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  type AuthSession,
  clearAuthSession,
  isSessionValid,
  loadAuthSession,
  saveAuthSession,
} from '../../domain/entities/AuthSession';
import { getAuthRepository } from '../../infrastructure/repositories/container';

// Same key written by useEnrollDeviceOnLogin.persistDeviceId — read directly
// here (no shared port) to notify the backend before the token is cleared.
const ENROLLED_DEVICE_KEY = 'mi_enrolled_device_id';

interface AuthContextValue {
  isAuthenticated: boolean;
  session: AuthSession | null;
  saveSession: (session: AuthSession) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<AuthSession | null>(() => {
    if (!isSessionValid()) return null;
    return loadAuthSession();
  });

  const saveSession = useCallback((s: AuthSession) => {
    saveAuthSession(s);   // persiste token en localStorage para el interceptor de httpClient
    setSession(s);
  }, []);

  const logout = useCallback(() => {
    void (async () => {
      // Notify the backend BEFORE clearing the session — the token in
      // localStorage (read by httpClient's request interceptor) must still
      // be valid when this request goes out. Best-effort: never blocks logout.
      const idDevice = localStorage.getItem(ENROLLED_DEVICE_KEY);
      if (idDevice) {
        try {
          await getAuthRepository().logoutDevice(idDevice);
        } catch {
          // ignore — offline/backend error must not block logout
        }
      }

      clearAuthSession();
      setSession(null);

      // Clear React Query cache to prevent showing previous user's data.
      // Without this, React Query serves stale profiles/employees/timelogs from
      // the in-memory cache even after IndexedDB and localStorage are cleared.
      queryClient.clear();

      // Notify listeners (e.g. ProfileProvider) so they can reset in-memory state
      // that the storage clear alone cannot reach. See profile-auto-selection R5.
      window.dispatchEvent(new Event('mi:auth-logout'));
    })();
  }, [queryClient]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: session !== null,
        session,
        saveSession,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
